import { createContext, useContext, useMemo } from "react";
import { ShopOverrideRuleDto } from "@babackapp/private-api-client/types";
import useCreateShopOverrideRule from "@babackapp/private-api-client/client/use-create-shop-override-rule";
import useUpdateShopOverrideRules from "@babackapp/private-api-client/client/use-update-shop-override-rules";
import { useParams } from "react-router-dom";
import useShopOverrideRules from "@babackapp/private-api-client/client/use-shop-override-rules";
import useDeleteShopOverrideRule from "@babackapp/private-api-client/client/use-delete-shop-override-rule";

interface OverrideRulesContextValue {
  rules: ShopOverrideRuleDto[];
  isLoading: boolean;
  error: unknown;
  createRule: ReturnType<typeof useCreateShopOverrideRule>["mutate"];
  updateRules: ReturnType<typeof useUpdateShopOverrideRules>["mutate"];
  deleteRule: (ruleId: string) => void;
  createRulePending: boolean;
  updateRulesPending: boolean;
  deleteRulePending: boolean;
  refetch?: () => void;
}

const OverrideRulesContext = createContext<
  OverrideRulesContextValue | undefined
>(undefined);

export function OverrideRulesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { shopId } = useParams<{ shopId: string }>();
  const {
    data: rules = [],
    isLoading,
    error,
    refetch,
  } = useShopOverrideRules(shopId);
  const afterUpdate = () => {
    refetch?.();
  };

  const deleteMutation = useDeleteShopOverrideRule(shopId || "", {
    onSuccess: afterUpdate,
  });
  const createMutation = useCreateShopOverrideRule(shopId || "", {
    onSuccess: afterUpdate,
  });
  const updateMutation = useUpdateShopOverrideRules(shopId || "", {
    onSuccess: afterUpdate,
  });

  const value = useMemo<OverrideRulesContextValue>(
    () => ({
      rules,
      isLoading,
      error,
      refetch,
      createRule: createMutation.mutate,
      createRulePending: createMutation.isPending,
      updateRules: updateMutation.mutate,
      updateRulesPending: updateMutation.isPending,
      deleteRule: (id: string) => deleteMutation.mutate(id),
      deleteRulePending: deleteMutation.isPending,
    }),
    [rules, isLoading, error],
  );

  return (
    <OverrideRulesContext.Provider value={value}>
      {children}
    </OverrideRulesContext.Provider>
  );
}

export function useOverrideRules() {
  const context = useContext(OverrideRulesContext);
  if (!context) {
    throw new Error(
      "useOverrideRules must be used within an OverrideRulesProvider",
    );
  }
  return context;
}
